import initialState from './initialState';
import { LoadingStatusEnum } from '../../../constants/enums';

const mutateTaskInListRecursive = (list: any[], updatedTask: any): boolean => {
  if (!list || !Array.isArray(list) || !updatedTask) {
    return false;
  }

  // Extract all nodes from the updated task tree into a Map
  const updatedNodesMap = new Map<string, any>();
  const extractNodes = (node: any) => {
    if (!node || !node._id) {
      return;
    }
    updatedNodesMap.set(node._id, node);
    if (node.subTasks && Array.isArray(node.subTasks)) {
      node.subTasks.forEach(extractNodes);
    }
  };
  extractNodes(updatedTask);

  let matchedAny = false;

  const syncList = (currentList: any[]) => {
    for (let i = 0; i < currentList.length; i++) {
      const currentTask = currentList[i];
      const updatedVersion = updatedNodesMap.get(currentTask._id);

      if (updatedVersion) {
        currentList[i] = { ...currentTask, ...updatedVersion };
        matchedAny = true;
      } else if (currentTask.subTasks && currentTask.subTasks.length > 0) {
        syncList(currentTask.subTasks);
      }
    }
  };

  syncList(list);
  return matchedAny;
};

const removeTaskFromListRecursive = (list: any[], taskId: string): any[] => {
  if (!list) {
    return [];
  }
  return list
    .filter((task) => task._id !== taskId)
    .map((task) => {
      if (task.subTasks && task.subTasks.length > 0) {
        return {
          ...task,
          subTasks: removeTaskFromListRecursive(task.subTasks, taskId),
        };
      }
      return task;
    });
};

export const GetEmployeeTasksReducer = {
  pending: (state) => ({
    ...state,
    loading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => ({
    ...state,
    list: action.payload.data || [],
    loading: LoadingStatusEnum.Succeeded,
  }),
  rejected: (state) => ({
    ...state,
    loading: LoadingStatusEnum.Failed,
  }),
};

export const GetTechnicalRawEmployeeTasksReducer = {
  pending: (state) => ({
    ...state,
    loading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => ({
    ...state,
    list: action.payload.data || [],
    loading: LoadingStatusEnum.Succeeded,
  }),
  rejected: (state) => ({
    ...state,
    loading: LoadingStatusEnum.Failed,
  }),
};

export const UpdateCigarProductionPlanTaskStatusReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => {
    const { data } = action.payload;

    if (data) {
      mutateTaskInListRecursive(state.list, data);
    }
    state.mutationLoading = LoadingStatusEnum.Succeeded;
  },
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const UpdateEmployeeTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => {
    const { data } = action.payload;

    if (data) {
      mutateTaskInListRecursive(state.list, data);
    }
    state.mutationLoading = LoadingStatusEnum.Succeeded;
  },
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const ArchiveCigarProductionPlanTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => {
    const { data } = action.payload;

    return {
      ...state,
      list: removeTaskFromListRecursive(state.list, data._id),
      mutationLoading: LoadingStatusEnum.Succeeded,
    };
  },
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const DeleteCigarProductionPlanTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => {
    const { data } = action.payload;

    return {
      ...state,
      list: removeTaskFromListRecursive(state.list, data._id),
      mutationLoading: LoadingStatusEnum.Succeeded,
    };
  },
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const CreateCycleCountForCigarProductionPlanTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Succeeded,
  }),
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const CreateCigarProductionScheduleTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Succeeded,
  }),
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const CreateTobaccoProductionPlanningTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Succeeded,
  }),
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const CreateEmployeeTaskReducer = {
  pending: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Pending,
  }),
  fulfilled: (state, action) => {
    const { data } = action.payload || {};

    return {
      ...state,
      list: data ? [data, ...state.list.filter((task) => task?._id !== data._id)] : state.list,
      mutationLoading: LoadingStatusEnum.Succeeded,
    };
  },
  rejected: (state) => ({
    ...state,
    mutationLoading: LoadingStatusEnum.Failed,
  }),
};

export const ResetEmployeeTasksStateReducer = () => initialState;

export const GetEmployeeTaskHistoryReducer = {
  pending: (state, action) => ({
    ...state,
    history: {
      taskId: action.meta.arg?.id ?? null,
      list: [],
      loading: LoadingStatusEnum.Pending,
    },
  }),
  fulfilled: (state, action) => ({
    ...state,
    history: {
      taskId: action.meta.arg?.id ?? null,
      list: action.payload.data || [],
      loading: LoadingStatusEnum.Succeeded,
    },
  }),
  rejected: (state, action) => ({
    ...state,
    history: {
      taskId: action.meta.arg?.id ?? null,
      list: [],
      loading: LoadingStatusEnum.Failed,
    },
  }),
};

export const ClearEmployeeTaskHistoryReducer = (state) => ({
  ...state,
  history: {
    taskId: null,
    list: [],
    loading: LoadingStatusEnum.Idle,
  },
});
